import { useState } from 'react';
import { useOperatorQuotingHours, useSetOperatorQuotingHours } from '../lib/api';
import { zoneOptions, zoneOptionLabel, shortWindow, actorName, draftFrom, draftProblem, saveBody, canSave, freshState, stateFor } from '../lib/quoting-hours-ui.mjs';

// SQP-332: per-operator quoting hours. Self-contained on purpose (data hooks + pure helpers only, no page
// state) so the SQP-302 Config redesign can lift it unchanged. It limits AUTOMATED quoting only.
export default function QuotingHoursControl({ operatorId }) {
  const { data, isLoading, isError, error } = useOperatorQuotingHours(operatorId);
  const save = useSetOperatorQuotingHours();
  // Draft state is tied to the operator it was made on (key={operatorId} at the call site as well).
  const [st, setSt] = useState(() => freshState(operatorId));
  const cur = stateFor(st, operatorId);
  const setEdit = edit => setSt({ ...cur, edit, confirm: false });

  const loaded = !!data && !isLoading && !isError;
  const setting = data?.setting || null;
  const invalid = loaded && data.valid === false;
  const base = draftFrom(setting);
  const draft = { ...base, ...(cur.edit || {}) };
  const problem = draftProblem(draft);
  const dirty = !!cur.edit && Object.keys(cur.edit).length > 0;
  const last = data?.last_change;
  const busy = save.isPending;
  const enabled = canSave({ loaded, hasSetting: !!setting, draft, base, busy });

  const onSave = () => {
    if (!enabled) return;
    save.mutate({ operatorId, body: saveBody(draft) }, { onSuccess: () => setSt(freshState(operatorId)) });
  };
  const onClear = () => save.mutate({ operatorId, body: { clear: true } }, { onSuccess: () => setSt(freshState(operatorId)) });

  let pill = null;
  let summary = null;
  if (isLoading) summary = <span className="text-label-secondary">Loading</span>;
  else if (isError) summary = <span className="text-critical">Could not load: {error?.message}</span>;
  else if (invalid) {
    pill = <span className="badge badge-critical">Invalid, quoting blocked</span>;
  } else if (!setting) {
    pill = <span className="badge badge-neutral">24/7</span>;
    summary = <span>24/7 &mdash; no quoting-hours limit</span>;
  } else {
    pill = data.within_now ? <span className="badge badge-ok">Open now</span> : <span className="badge badge-info">Closed</span>;
    summary = (
      <span>
        <span className="font-medium">{data.window_label}</span>
        <span className="text-label-secondary"> &middot; </span>
        {data.within_now ? 'Open now' : <>Closed &mdash; opens {data.opens_label || setting.start}</>}
      </span>
    );
  }

  const field = 'w-full min-h-[40px] px-2 py-1 text-base sm:text-sm bg-surface text-label border border-slate-300 rounded-md';

  return (
    <div className="card p-4" data-testid="quoting-hours">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="font-semibold text-sm">Quoting hours</div>
        {pill}
      </div>
      <p className="text-xs text-label-secondary mb-3">
        Limits automated quoting only. A request that arrives outside the window waits and is quoted when it opens,
        every day including weekends. People can still send a quote by hand at any time.
      </p>

      {invalid && (
        <div role="alert" className="text-xs bg-critical-fill text-critical rounded p-2 mb-3">
          The stored quoting hours are invalid{data.invalid_reason ? <> ({data.invalid_reason})</> : null}.
          Automated quoting is blocked for this operator until you save a valid window or remove the limit.
        </div>
      )}

      {summary && <div className="text-sm mb-3">{summary}</div>}
      {invalid && <div className="text-xs text-label-secondary mb-2">The fields below show defaults, not the stored value.</div>}

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 mb-3">
        <label className="block text-xs text-label-secondary">
          Time zone
          <select className={field + ' mt-1'} value={draft.tz} disabled={busy} onChange={e => setEdit({ ...(cur.edit || {}), tz: e.target.value })}>
            {zoneOptions(draft.tz).map(z => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
          </select>
        </label>
        <label className="block text-xs text-label-secondary">
          Opens
          <input type="time" className={field + ' mt-1 font-mono'} value={draft.start} disabled={busy} onChange={e => setEdit({ ...(cur.edit || {}), start: e.target.value })} />
        </label>
        <label className="block text-xs text-label-secondary">
          Closes
          <input type="time" className={field + ' mt-1 font-mono'} value={draft.end} disabled={busy} onChange={e => setEdit({ ...(cur.edit || {}), end: e.target.value })} />
        </label>
      </div>

      {(dirty || !setting) && problem && <div className="text-xs text-attention mb-2">{problem}</div>}
      {save.isError && <div role="alert" className="text-xs text-critical mb-2">Not saved: {save.error?.message}</div>}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onSave} disabled={!enabled}
          className="btn btn-primary min-h-[40px] active:scale-[0.97] transition-transform duration-150">
          {busy ? 'Saving' : 'Save hours'}
        </button>
        {loaded && (setting || invalid) && !cur.confirm && (
          <button type="button" onClick={() => setSt({ ...cur, confirm: true })} disabled={busy}
            className="btn btn-secondary min-h-[40px] active:scale-[0.97] transition-transform duration-150">
            Remove limit (24/7)
          </button>
        )}
        {loaded && (setting || invalid) && cur.confirm && (
          <>
            <button type="button" onClick={onClear} disabled={busy}
              className="btn btn-danger min-h-[40px] active:scale-[0.97] transition-transform duration-150">
              Confirm remove
            </button>
            <button type="button" onClick={() => setSt({ ...cur, confirm: false })} disabled={busy}
              className="btn btn-secondary min-h-[40px] active:scale-[0.97] transition-transform duration-150">
              Cancel
            </button>
          </>
        )}
      </div>

      {last && (
        <div className="text-xs text-label-secondary mt-3" data-testid="quoting-hours-last">
          Last change: <span className="font-mono">{actorName(last.actor)}</span> at {last.at}
          {!last.details_unreadable && <>{' '}&mdash; {shortWindow(last.old)} &rarr; {shortWindow(last.new)}</>}
        </div>
      )}
    </div>
  );
}
